// Refund-amount math shared by the agent UI and the server (2026-10-14): the
// task flows' Confirm Refund Value check (server/src/ai/task-flows.js)
// compares a return document's refund total with the same per-line amounts
// the UI shows. Moved here unchanged from ui/src/refundAmount.js and
// ui/src/itemTax.js, which re-export them.

// computeUnitRefundAmounts() — per-unit refund amount for each line, mirrored
// from "RefundAmountCalcs.xlsx". A standalone or kit line refunds at its own
// unitPrice. A component line has no price of its own (TRQ carries a kit's
// full price on the kit line only) — it inherits a share of its PARENT kit's
// unitPrice, proportional to its own share of the kit's unitCost:
//   componentUnitRefundAmt = parentKit.unitPrice * (component.unitCost / parentKit.unitCost)
//
// Parent-kit join: same lineSequence + itemType === 'kit' sibling lookup
// SuggestionPanel.jsx's isComponentBlockedByKit()/toggleKitAwareSelection()
// and return-refund.js's isPartialSelection() already use, and
// computeLineTax() below uses since 2026-10-30. Never positional: TRQ does
// not always list a kit's components right under it.
//
// Returns an array parallel to `items`. null for a component whose parent
// kit can't be resolved, or whose parent's own unitCost is 0/null/absent
// (the ratio above would divide by zero) — never a fabricated guess.
export function computeUnitRefundAmounts(items) {
  if (!Array.isArray(items)) return []
  const kitsByLineSequence = new Map()
  for (const it of items) {
    if (it.itemType === 'kit' && it.lineSequence != null) kitsByLineSequence.set(it.lineSequence, it)
  }
  return items.map((it) => {
    if (it.itemType !== 'component') return Number(it.unitPrice) || 0
    const parentKit = it.lineSequence != null ? kitsByLineSequence.get(it.lineSequence) : null
    if (!parentKit) return null
    const kitUnitCost = Number(parentKit.unitCost) || 0
    if (!kitUnitCost) return null
    const kitUnitPrice = Number(parentKit.unitPrice) || 0
    const componentUnitCost = Number(it.unitCost) || 0
    return kitUnitPrice * (componentUnitCost / kitUnitCost)
  })
}

// Non-inventory lines (2026-10-30, Jenn): a line that is not a stocked part,
// like the Retail Delivery Fee (SKU DELIVERYFEE). It takes its share of the
// order's tax like any other line (the Console does the same), but it is
// never selected for a return by default, never added by the AI, and added to
// a refund only when a person picks it. TRQ's order item gives no item-type
// or inventory flag for it (the line items carry sku, kit,
// componentSequence, lineSequence, unitPrice, unitCost, extendedPrice and the
// like; nothing in server/src/integrations/trq/ or docs/trq-api.md marks a
// non-inventory line), so the SKU decides, from this list. DELIVERYFEE is the
// only one (Jenn, 2026-10-30). Matched trimmed and case-insensitive.
export const NON_INVENTORY_SKUS = Object.freeze(['DELIVERYFEE'])

const NON_INVENTORY_SET = new Set(NON_INVENTORY_SKUS)

// True for a non-inventory order line (or a bare SKU string). A component is
// part of a kit, so never non-inventory on its own.
export function isNonInventoryLine(itemOrSku) {
  if (itemOrSku == null) return false
  if (typeof itemOrSku === 'object' && itemOrSku.itemType === 'component') return false
  const sku = typeof itemOrSku === 'object' ? itemOrSku.sku : itemOrSku
  if (sku == null) return false
  return NON_INVENTORY_SET.has(String(sku).trim().toUpperCase())
}

// The selection with every non-inventory line removed (see above).
// `selection` is [{ sku, quantity }] or order lines.
export function withoutNonInventory(selection) {
  return (selection ?? []).filter((s) => !isNonInventoryLine(s))
}

// Pure per-line sales-tax allocation, the one tax split behind every refund
// amount CXHub works out (the All Items window's Unit Tax column, the refund
// breakdown, the return step after Check, the agent's calculate_refund, the
// reply's refund lines, the Site Automation and task refund checks, the
// shadow preview). Mirrored from the reference workbook ("sales tax
// calculations.xlsx") and matched to the Console (2026-10-30):
//
//   orderSubtotal  = orderTotal - orderTax - shippingCost
//   kit/standalone = (unitPrice * quantity / orderSubtotal) * orderTax
//   component      = (unitCost / (kit.unitCost * kit.quantity)) * kitLineTax * quantity
//
// Every line takes its price share of the order's tax, a non-inventory line
// (the Retail Delivery Fee) included, as the Console does. A component takes
// its tax from ITS OWN kit: the kit line with the same lineSequence, the join
// computeUnitRefundAmounts() uses. Never by position (2026-10-30): TRQ does
// not always list a kit's components right under it (a delivery fee line can
// sit between them, and the old walk taxed the components off the fee).
//
// orderTax/shippingCost/orderTotal are the workbook's order-level inputs.
// Returns an array parallel to `items`: the tax of items[i]'s WHOLE line (all
// of its quantity) at result[i]. null wherever the inputs for that line
// aren't resolvable (missing orderTax/orderTotal, a zero subtotal or kit
// cost, or a component with no kit line of its own lineSequence), never a
// fabricated 0.
export function computeLineTax(items, { orderTax, shippingCost, orderTotal } = {}) {
  if (!Array.isArray(items)) return []
  if (orderTax == null || orderTotal == null) return items.map(() => null)
  const orderSubtotal = orderTotal - orderTax - (shippingCost ?? 0)

  const ownLineTax = (it) => {
    if (!orderSubtotal) return null
    return (((Number(it.unitPrice) || 0) * (Number(it.quantity) || 0)) / orderSubtotal) * orderTax
  }

  const kitsByLineSequence = new Map()
  for (const it of items) {
    if (it.itemType === 'kit' && it.lineSequence != null) kitsByLineSequence.set(it.lineSequence, it)
  }

  return items.map((it) => {
    if (it.itemType !== 'component') return ownLineTax(it)
    const kit = it.lineSequence != null ? kitsByLineSequence.get(it.lineSequence) : null
    if (!kit) return null
    const kitLineTax = ownLineTax(kit)
    if (kitLineTax == null) return null
    const kitExtendedCost = (Number(kit.unitCost) || 0) * (Number(kit.quantity) || 0)
    if (!kitExtendedCost) return null
    const pctOfKit = (Number(it.unitCost) || 0) / kitExtendedCost
    return pctOfKit * kitLineTax * (Number(it.quantity) || 0)
  })
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100

// estimatedRefundBreakdown() — the items+tax refund for exactly the selected
// items ({ sku, quantity }), the one refund sum every caller uses (the panel
// after Check, refund-amount-fill.js, task-refund-amount.js, the shadow
// preview). computeLineTax() is walked over the FULL allItems list (it needs
// every kit line to split a kit's tax among its components), then per
// selected line:
//
//   items = its unit refund amount (computeUnitRefundAmounts(), so a
//           component refunds at its share of its kit's unitPrice) * the
//           SELECTED quantity
//   tax   = its unit tax (the line's tax / the line's quantity) * the
//           SELECTED quantity, so 1 of 2 takes half the line's tax
//           (2026-10-30; before, a partial quantity took the whole line's
//           tax)
//
// The Console's rounding (2026-10-30, a prod RTN: $83.16 + $6.94 = $90.10):
// the items and the tax are each summed over the selected lines, each sum
// rounded to the cent, then added. The unit tax itself is not rounded.
// deduction is the label cost to subtract (0 when nothing is deducted).
//
// Returns { itemsAmount, taxAmount, itemsTotal (itemsAmount + taxAmount),
// deductionAmount, estimatedRefund, unresolvedSkus, lines }. lines: per
// selected line { sku, quantity, items, tax }, unrounded. unresolvedSkus: the
// selected line(s) whose Unit Refund Amt couldn't be resolved
// (computeUnitRefundAmounts returned null), left out of the sums rather than
// counted as $0, so the caller can name them. A line whose tax can't be
// resolved adds no tax; a caller that must not guess checks lineTaxes first.
export function estimatedRefundBreakdown({ allItems, selectedItems, lineTaxes, deduction }) {
  const unitRefundAmts = computeUnitRefundAmounts(allItems)
  let items = 0
  let tax = 0
  const unresolvedSkus = []
  const lines = []
  ;(allItems ?? []).forEach((it, idx) => {
    const entry = (selectedItems ?? []).find((s) => s.sku === it.sku)
    if (!entry) return
    const unitRefundAmt = unitRefundAmts[idx]
    if (unitRefundAmt == null) {
      if (it.sku) unresolvedSkus.push(it.sku)
      return
    }
    const qty = Number(entry.quantity) || 0
    const lineQty = Number(it.quantity) || 0
    const lineTax = lineTaxes?.[idx]
    const lineItems = unitRefundAmt * qty
    const lineTaxPart = lineTax == null || !lineQty ? 0 : (lineTax / lineQty) * qty
    items += lineItems
    tax += lineTaxPart
    lines.push({ sku: it.sku, quantity: qty, items: lineItems, tax: lineTaxPart })
  })
  const itemsAmount = round2(items)
  const taxAmount = round2(tax)
  const itemsTotal = round2(itemsAmount + taxAmount)
  const deductionAmount = Number(deduction) || 0
  return { itemsAmount, taxAmount, itemsTotal, deductionAmount, estimatedRefund: round2(itemsTotal - deductionAmount), unresolvedSkus, lines }
}
